import React from 'react'
import { PageSection, PageContent } from '../lib/storage'
import { AlertTriangle, Info, CheckCircle, Mail, Building } from 'lucide-react'

interface PageContentRendererProps {
  content: PageContent
  systemSettings?: any
}

const PageContentRenderer: React.FC<PageContentRendererProps> = ({ content, systemSettings }) => {
  const renderSection = (section: PageSection) => {
    switch (section.type) {
      case 'heading':
        return renderHeading(section)
      case 'paragraph':
        return renderParagraph(section)
      case 'list':
        return renderList(section)
      case 'warning':
        return renderWarning(section)
      case 'info':
        return renderInfo(section)
      case 'contact':
        return renderContact(section)
      default:
        return null
    }
  }

  const renderHeading = (section: PageSection) => {
    const level = section.level || 2
    const HeadingTag = `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
    const classNames: Record<number, string> = {
      2: 'text-2xl font-bold text-gray-900 mt-8 mb-4',
      3: 'text-lg font-semibold text-gray-900 mt-6 mb-3',
      4: 'text-base font-medium text-gray-900 mt-4 mb-2'
    }

    return React.createElement(
      HeadingTag,
      { key: section.id, className: classNames[level] },
      section.title
    )
  }

  const renderParagraph = (section: PageSection) => {
    return (
      <p key={section.id} className="text-gray-700 mb-4 leading-relaxed">
        {section.content}
      </p>
    )
  }

  const renderList = (section: PageSection) => {
    return (
      <ul key={section.id} className="list-disc list-inside space-y-2 mb-6 text-gray-700">
        {section.items?.map((item, index) => (
          <li key={`${section.id}-${index}`}>{item}</li>
        ))}
      </ul>
    )
  }

  const renderWarning = (section: PageSection) => {
    const bgColor = section.severity === 'error' ? 'bg-red-50' : 'bg-yellow-50'
    const borderColor = section.severity === 'error' ? 'border-red-200' : 'border-yellow-200'
    const iconColor = section.severity === 'error' ? 'text-red-600' : 'text-yellow-600'
    const titleColor = section.severity === 'error' ? 'text-red-800' : 'text-yellow-800'
    const textColor = section.severity === 'error' ? 'text-red-700' : 'text-yellow-700'

    return (
      <div key={section.id} className={`${bgColor} border ${borderColor} rounded-lg p-6 my-6`}>
        <div className="flex items-start space-x-3">
          <AlertTriangle className={`h-6 w-6 ${iconColor} mt-1 flex-shrink-0`} />
          <div>
            {section.title && (
              <h3 className={`text-lg font-semibold ${titleColor} mb-2`}>{section.title}</h3>
            )}
            {section.content && (
              <p className={textColor}>{section.content}</p>
            )}
            {section.items && (
              <ul className={`space-y-2 ${textColor} mt-3`}>
                {section.items.map((item, index) => (
                  <li key={`${section.id}-${index}`}>• {item}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderInfo = (section: PageSection) => {
    const bgColor = section.severity === 'warning' ? 'bg-amber-50' : 'bg-blue-50'
    const borderColor = section.severity === 'warning' ? 'border-amber-200' : 'border-blue-200'
    const iconColor = section.severity === 'warning' ? 'text-amber-600' : 'text-blue-600'
    const titleColor = section.severity === 'warning' ? 'text-amber-900' : 'text-blue-900'
    const textColor = section.severity === 'warning' ? 'text-amber-800' : 'text-blue-800'

    return (
      <div key={section.id} className={`${bgColor} border ${borderColor} rounded-lg p-6 my-6`}>
        {section.title && (
          <h3 className={`text-lg font-semibold ${titleColor} mb-3`}>{section.title}</h3>
        )}
        {section.content && (
          <p className={textColor}>{section.content}</p>
        )}
        {section.items && (
          <ul className="space-y-2 text-blue-800 mt-3">
            {section.items.map((item, index) => (
              <li key={`${section.id}-${index}`} className="flex items-start">
                <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }

  const renderContact = (section: PageSection) => {
    const metadata = content.metadata
    const email = metadata?.contactEmail || systemSettings?.supportEmail || 'support@netlify.com'
    const address = metadata?.contactAddress || systemSettings?.companyLocation || '512 2nd Street, Floor 2, San Francisco, CA 94107'
    const companyName = systemSettings?.companyName || 'Netlify, Inc'

    return (
      <div key={section.id} className="bg-gray-50 p-6 rounded-lg mt-4">
        <div className="space-y-3">
          {email && (
            <div className="flex items-center">
              <Mail className="h-5 w-5 text-gray-500 mr-3" />
              <div>
                <strong>Email:</strong>
                <br />
                <a href={`mailto:${email}`} className="text-primary-600 hover:text-primary-700">
                  {email}
                </a>
              </div>
            </div>
          )}
          {address && (
            <div className="flex items-start">
              <Building className="h-5 w-5 text-gray-500 mr-3 mt-1" />
              <div>
                <strong>Mailing Address:</strong>
                <br />
                {companyName}
                <br />
                {section.title && (
                  <>
                    {section.title}
                    <br />
                  </>
                )}
                {address}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="prose prose-lg max-w-none">
      {(content.sections || [])
        .sort((a, b) => a.order - b.order)
        .map(section => renderSection(section))}
    </div>
  )
}

export default PageContentRenderer